import { useRef, useState, useEffect } from "react";
import { onVenueSingleImageUpload } from "../../../../Store/Slices/venueImageSlice";

function Step4VenueImages({
  coverFile,
  coverImageId,
  galleryFiles,
  galleryImageIds,
  uploadingSlot,
  errors,
  savedImages,
  coverInputRef,
  galleryInputRefs,
  handleRemoveStep4Image,
  handleConfirmDeleteImage,
  setCoverFile,
  setGalleryFiles,
  setUploadingSlot,
  dispatch,
  businessData,
  pendingReplaceCoverId,
  pendingReplaceGalleryIds,
  setErrors,
  addGallerySlot,
  addGallerySlotsWithFiles,
  readOnly = false,
}) {
  const multiFileInputRef = useRef(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { slot: 'cover'|number, imageId }
  const [deletingInProgress, setDeletingInProgress] = useState(false);

  useEffect(() => {
    if (!uploadingSlot) setDeletingInProgress(false);
  }, [uploadingSlot]);
  const galleryCount = Math.max(
    4,
    galleryFiles?.length ?? 4,
    galleryImageIds?.length ?? 4,
  );
  const galleryIndices = Array.from({ length: galleryCount }, (_, i) => i);

  const coverLoading = uploadingSlot?.type === "cover";
  if (readOnly) {
    const coverImg = savedImages?.find((i) => i.id === coverImageId);
    const galleryImgs = galleryIndices.map((i) => {
      const id = galleryImageIds?.[i];
      return id ? savedImages?.find((s) => s.id === id) : null;
    });
    return (
      <div className="step4-form step1-form step4-readonly">
        <div className="step4-cover">
          <label className="form-label">Cover image</label>
          <div className="image-upload-box">
            {coverImg ? (
              <img
                src={coverImg.image_url || coverImg.url || coverImg.path}
                alt="Cover"
                className="image-preview"
              />
            ) : (
              <span className="step4-no-image">No cover image</span>
            )}
          </div>
        </div>
        <div className="step4-gallery">
          <label className="form-label">Gallery images</label>
          <div className="gallery-upload-grid">
            {galleryIndices.map((i) => (
              <div key={i} className="image-upload-box">
                {galleryImgs[i] ? (
                  <img
                    src={galleryImgs[i].image_url || galleryImgs[i].url || galleryImgs[i].path}
                    alt={`Gallery ${i + 1}`}
                    className="image-preview"
                  />
                ) : (
                  <span className="step4-no-image">—</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  const handleMultiSelect = (e) => {
    const files = e.target.files;
    if (files?.length && addGallerySlotsWithFiles) {
      addGallerySlotsWithFiles(files);
      if (setErrors && errors?.gallery) setErrors((prev) => ({ ...prev, gallery: "" }));
    }
    e.target.value = "";
  };

  return (
    <div className="step4-form step1-form">
      <p className="step-note">
        Upload at least 1 cover image and 4 gallery images (5 minimum). You can add more and select multiple at once.
      </p>
      <div className="step4-cover">
        <label className="form-label">
          Cover image <span className="required">*</span>
        </label>
        <div
          className={`image-upload-box${coverLoading ? " step4-slot-loading" : ""}`}
        >
          {coverLoading ? (
            <div className="step4-slot-loader-overlay" aria-hidden="true">
              <div className="step4-slot-spinner" />
              <span className="step4-slot-loader-text">
                {deletingInProgress ? "Deleting..." : "Uploading..."}
              </span>
            </div>
          ) : null}
          <input
            ref={coverInputRef}
            type="file"
            accept="image/*"
            className="image-upload-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              setCoverFile(file || null);
              if (errors.cover) setErrors((prev) => ({ ...prev, cover: "" }));
              if (file) {
                setUploadingSlot({ type: "cover" });
                dispatch(
                  onVenueSingleImageUpload({
                    businessId: businessData?.id,
                    file,
                    type: "cover",
                    galleryIndex: null,
                    imageId:
                      coverImageId ??
                      pendingReplaceCoverId.current ??
                      undefined,
                  }),
                );
              }
            }}
          />
          {coverFile || coverImageId ? (
            <div className="image-preview-wrap">
              {coverFile ? (
                <img
                  src={URL.createObjectURL(coverFile)}
                  alt="Cover"
                  className="image-preview"
                />
              ) : (
                coverImageId &&
                (() => {
                  const img = savedImages.find((i) => i.id === coverImageId);
                  return img ? (
                    <img
                      src={img.image_url || img.url || img.path}
                      alt="Cover"
                      className="image-preview"
                    />
                  ) : (
                    <div className="image-preview image-preview-uploaded">
                      Uploaded
                    </div>
                  );
                })()
              )}
              <span className="image-preview-label">Cover</span>
              <button
                type="button"
                className="image-preview-remove"
                onClick={() => {
                  if (coverImageId != null && handleConfirmDeleteImage) {
                    setDeleteTarget({ slot: "cover", imageId: coverImageId });
                  } else {
                    handleRemoveStep4Image("cover");
                  }
                }}
                aria-label="Remove cover image"
              >
                ×
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="image-upload-placeholder"
              onClick={() => coverInputRef.current?.click()}
            >
              + Add cover image
            </button>
          )}
        </div>
        {errors.cover && <span className="error-message">{errors.cover}</span>}
      </div>
      <div className="step4-gallery">
        <label className="form-label">
          Gallery images (4 required) <span className="required">*</span>
        </label>
        <div className="step4-gallery-actions">
          {addGallerySlot && (
            <button
              type="button"
              className="step4-add-more-btn"
              onClick={addGallerySlot}
            >
              + Add one more image
            </button>
          )}
          {addGallerySlotsWithFiles && (
            <>
              <input
                ref={multiFileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="image-upload-input step4-multi-input"
                onChange={handleMultiSelect}
                aria-label="Select multiple images"
              />
              <button
                type="button"
                className="step4-multi-select-btn"
                onClick={() => multiFileInputRef.current?.click()}
              >
                Select multiple images
              </button>
            </>
          )}
        </div>
        <div className="gallery-upload-grid">
          {galleryIndices.map((i) => {
            const isSlotLoading =
              uploadingSlot?.type === "gallery" &&
              (Array.isArray(uploadingSlot?.indices)
                ? uploadingSlot.indices.includes(i)
                : uploadingSlot?.index === i);
            return (
            <div
              key={i}
              className={`image-upload-box${isSlotLoading ? " step4-slot-loading" : ""}`}
            >
              {isSlotLoading && (
                  <div className="step4-slot-loader-overlay" aria-hidden="true">
                    <div className="step4-slot-spinner" />
                    <span className="step4-slot-loader-text">
                      {deletingInProgress ? "Deleting..." : "Uploading..."}
                    </span>
                  </div>
                )}
              <input
                ref={(el) => (galleryInputRefs.current[i] = el)}
                type="file"
                accept="image/*"
                className="image-upload-input"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  setGalleryFiles((prev) => {
                    const next = [...prev];
                    next[i] = file || null;
                    return next;
                  });
                  if (errors.gallery)
                    setErrors((prev) => ({
                      ...prev,
                      gallery: "",
                    }));
                  if (file) {
                    setUploadingSlot({
                      type: "gallery",
                      index: i,
                    });
                    dispatch(
                      onVenueSingleImageUpload({
                        businessId: businessData?.id,
                        file,
                        type: "gallery",
                        galleryIndex: i,
                        imageId:
                          galleryImageIds[i] ??
                          pendingReplaceGalleryIds.current[i] ??
                          undefined,
                      }),
                    );
                  }
                }}
              />
              {galleryFiles[i] || galleryImageIds[i] ? (
                <div className="image-preview-wrap">
                  {galleryFiles[i] ? (
                    <img
                      src={URL.createObjectURL(galleryFiles[i])}
                      alt={`Gallery ${i + 1}`}
                      className="image-preview"
                    />
                  ) : (
                    galleryImageIds[i] &&
                    (() => {
                      const img = savedImages.find(
                        (s) => s.id === galleryImageIds[i],
                      );
                      return img ? (
                        <img
                          src={img.image_url || img.url || img.path}
                          alt={`Gallery ${i + 1}`}
                          className="image-preview"
                        />
                      ) : (
                        <div className="image-preview image-preview-uploaded">
                          Uploaded
                        </div>
                      );
                    })()
                  )}
                  <span className="image-preview-label">{i + 1}</span>
                  <button
                    type="button"
                    className="image-preview-remove"
                    onClick={() => {
                      if (galleryImageIds[i] != null && handleConfirmDeleteImage) {
                        setDeleteTarget({ slot: i, imageId: galleryImageIds[i] });
                      } else {
                        handleRemoveStep4Image(i);
                      }
                    }}
                    aria-label={`Remove gallery image ${i + 1}`}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="image-upload-placeholder"
                  onClick={() => galleryInputRefs.current[i]?.click()}
                >
                  + Image {i + 1}
                </button>
              )}
            </div>
          );
          })}
        </div>
        {errors.gallery && (
          <span className="error-message">{errors.gallery}</span>
        )}
      </div>

      {deleteTarget && (
        <div
          className="step4-delete-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="step4-delete-modal-title"
        >
          <div className="step4-delete-modal">
            <h3 id="step4-delete-modal-title" className="step4-delete-modal-title">
              Delete this image?
            </h3>
            <p className="step4-delete-modal-text">
              This image will be removed from your profile. This cannot be undone.
            </p>
            <div className="step4-delete-modal-actions">
              <button
                type="button"
                className="step4-delete-modal-btn step4-delete-modal-cancel"
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="step4-delete-modal-btn step4-delete-modal-confirm"
                onClick={() => {
                  if (handleConfirmDeleteImage && deleteTarget?.imageId != null) {
                    handleConfirmDeleteImage(deleteTarget.slot, deleteTarget.imageId);
                    setDeletingInProgress(true);
                    setDeleteTarget(null);
                  }
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Step4VenueImages;
